import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Building2, ChevronLeft, ChevronRight, MapPin, Users, CheckCircle2, Calendar, Clock, FileText, Car } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { parkingConfigurationApi } from '../services/api'
import { showError } from '../utils/showError'

const wizardCrumb = (event, data) =>
  Sentry.addBreadcrumb({
    category: 'wizard.parking-setup',
    message: event,
    level: event === 'error' ? 'error' : 'info',
    data,
  })
import {
  PageHeader,
  EmptyState,
  Card,
  CardBody,
  Button,
  FormField,
  FormTextarea,
  FormCheckbox,
  LoadingSpinner,
  SuccessMessage,
} from '../components/common'

// Mirror server enum values
const PARKING_TYPE = {
  SPECIFIC_SPOT: 1,
  LIMITED_OPEN: 2,
  FREE_PARKING: 3,
}

const STEPS = ['parkingType', 'reservationMode', 'limits', 'rules', 'review']

const initialState = {
  parkingType: undefined,
  totalCapacity: '',
  requiresAdvanceReservation: undefined,
  maxReservationHours: '',
  reservationWindowDays: '',
  perUnitEnabled: false,
  perUnitWeekly: '',
  perUnitMonthly: '',
  perUnitActive: '',
  perPlateEnabled: false,
  perPlateWeekly: '',
  perPlateMonthly: '',
  rules: '',
}

const ParkingSetupWizard = () => {
  const { selectedBuildingId } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState('')
  const [step, setStep] = useState(0)
  const [state, setState] = useState(initialState)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!selectedBuildingId) {
      setLoading(false)
      return
    }
    loadConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId])

  const loadConfig = async () => {
    try {
      setLoading(true)
      const response = await parkingConfigurationApi.get(selectedBuildingId)
      const data = response.data?.data || response.data

      // Already set up — bounce to the regular settings panel.
      if (data && data.parkingConfigurationId && data.isSetupCompleted) {
        navigate('/parking', { replace: true })
        return
      }

      // Pre-fill from prior partial save (so a manager can resume).
      if (data && data.parkingConfigurationId) {
        const perUnit = data.maxReservationsPerResidentWeekly != null
          || data.maxReservationsPerResidentMonthly != null
          || data.maxActiveReservationsPerUnit != null
        const perPlate = data.maxGuestVisitsPerPlateWeekly != null
          || data.maxGuestVisitsPerPlateMonthly != null
        setState({
          parkingType: data.parkingType,
          totalCapacity: data.totalCapacity ?? '',
          requiresAdvanceReservation: data.requiresAdvanceReservation,
          maxReservationHours: data.maxReservationHours ?? '',
          reservationWindowDays: data.reservationWindowDays ?? '',
          perUnitEnabled: perUnit,
          perUnitWeekly: data.maxReservationsPerResidentWeekly ?? '',
          perUnitMonthly: data.maxReservationsPerResidentMonthly ?? '',
          perUnitActive: data.maxActiveReservationsPerUnit ?? '',
          perPlateEnabled: perPlate,
          perPlateWeekly: data.maxGuestVisitsPerPlateWeekly ?? '',
          perPlateMonthly: data.maxGuestVisitsPerPlateMonthly ?? '',
          rules: data.rules ?? '',
        })
      }
    } catch (err) {
      console.error('Error loading parking config:', err)
    } finally {
      setLoading(false)
    }
  }

  // Free Parking skips reservation-mode + limits entirely (steps 1 and 2).
  const isFreeFlow = state.parkingType === PARKING_TYPE.FREE_PARKING
  const totalSteps = isFreeFlow ? 3 : 5
  // Map internal step index to displayed step number for the free-flow case.
  const displayedStep = isFreeFlow
    ? (step === 0 ? 1 : step === 3 ? 2 : step === 4 ? 3 : 1)
    : step + 1

  function update(partial) {
    setState((prev) => ({ ...prev, ...partial }))
    setError('')
  }

  useEffect(() => {
    wizardCrumb('enter', { step: displayedStep })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  function goNext() {
    setError('')
    if (step === 0) {
      if (state.parkingType == null) return
      if (state.parkingType === PARKING_TYPE.LIMITED_OPEN) {
        const n = parseInt(state.totalCapacity)
        if (!Number.isInteger(n) || n <= 0) {
          setError('Enter the number of available spots (positive whole number).')
          return
        }
      }
      if (state.parkingType === PARKING_TYPE.FREE_PARKING) {
        setStep(3) // jump to rules
        return
      }
      setStep(1)
      return
    }
    if (step === 1) {
      if (state.requiresAdvanceReservation == null) return
      if (state.requiresAdvanceReservation === false) {
        const n = parseInt(state.maxReservationHours)
        if (!Number.isInteger(n) || n <= 0) {
          setError('On-Arrival check-in requires a maximum reservation length.')
          return
        }
      } else if (state.maxReservationHours !== '' && state.maxReservationHours != null) {
        const n = parseInt(state.maxReservationHours)
        if (!Number.isInteger(n) || n <= 0) {
          setError('Maximum hours must be a positive whole number.')
          return
        }
      }
      if (state.requiresAdvanceReservation === true && state.reservationWindowDays !== '' && state.reservationWindowDays != null) {
        const n = parseInt(state.reservationWindowDays)
        if (!Number.isInteger(n) || n <= 0) {
          setError('Reservation window must be a positive whole number.')
          return
        }
      }
      setStep(2)
      return
    }
    if (step === 2) {
      setStep(3)
      return
    }
    if (step === 3) {
      setStep(4)
      return
    }
  }

  function goBack() {
    setError('')
    if (step === 0) {
      navigate('/parking')
      return
    }
    if (step === 4 && isFreeFlow) {
      setStep(3)
      return
    }
    if (step === 3 && isFreeFlow) {
      setStep(0)
      return
    }
    setStep(step - 1)
  }

  async function handleFinish() {
    if (state.parkingType == null) return
    wizardCrumb('finish', {
      parkingType: state.parkingType,
      isFreeFlow,
      perUnitEnabled: state.perUnitEnabled,
      perPlateEnabled: state.perPlateEnabled,
      hasRules: !!state.rules?.trim(),
    })
    try {
      setSubmitting(true)
      const toIntOrNull = (val) => {
        if (val === '' || val == null) return null
        const num = parseInt(val)
        return isNaN(num) ? null : num
      }
      await parkingConfigurationApi.set({
        buildingId: selectedBuildingId,
        parkingType: state.parkingType,
        totalCapacity: state.parkingType === PARKING_TYPE.LIMITED_OPEN ? toIntOrNull(state.totalCapacity) : null,
        rules: state.rules || null,
        // Visitor parking is always 24/7 — never expose operating hours.
        startTime: null,
        endTime: null,
        isAvailable24_7: true,
        // Free parking has no reservations — default advance=true, ignored server-side.
        requiresAdvanceReservation: isFreeFlow ? true : (state.requiresAdvanceReservation ?? true),
        maxReservationHours: isFreeFlow ? null : toIntOrNull(state.maxReservationHours),
        reservationWindowDays: isFreeFlow ? null : (state.requiresAdvanceReservation ? toIntOrNull(state.reservationWindowDays) : null),
        // Limits — only persist if the toggle was on.
        maxReservationsPerResidentWeekly: state.perUnitEnabled ? toIntOrNull(state.perUnitWeekly) : null,
        maxReservationsPerResidentMonthly: state.perUnitEnabled ? toIntOrNull(state.perUnitMonthly) : null,
        maxActiveReservationsPerUnit: state.perUnitEnabled ? toIntOrNull(state.perUnitActive) : null,
        maxGuestVisitsPerPlateWeekly: state.perPlateEnabled ? toIntOrNull(state.perPlateWeekly) : null,
        maxGuestVisitsPerPlateMonthly: state.perPlateEnabled ? toIntOrNull(state.perPlateMonthly) : null,
        // Per spec: per-vehicle "active reservations at once" is hidden + force-set to 1.
        maxActiveReservationsPerPlate: state.perPlateEnabled ? 1 : null,
        // Marks the wizard as done.
        isSetupCompleted: true,
        // Existing field on the proto — leave at default for visitor parking.
        checkInExpiryHours: null,
      })

      setSuccess('Parking setup saved successfully')

      // Assigned spots → redirect to spots tab so manager can add bays next.
      // Shared / no-reservation → return to parking hub.
      setTimeout(() => {
        navigate('/parking', { replace: true })
      }, 600)
    } catch (err) {
      wizardCrumb('error', { phase: 'save' })
      showError(err)
    } finally {
      setSubmitting(false)
    }
  }

  if (!selectedBuildingId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Visitor Parking Setup" subtitle="Configure visitor parking for this building" />
        <EmptyState
          icon={Building2}
          title="Select a building"
          description="Choose a building from the top bar to set up its parking."
        />
      </div>
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Visitor Parking Setup"
        subtitle="Tell residents how visitors park here"
      />

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="text-xs text-gray-500">Step {displayedStep} of {totalSteps}</div>
        <div className="flex gap-1.5">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full ${i < displayedStep ? 'bg-primary-500' : 'bg-gray-200'}`}
            />
          ))}
        </div>
      </div>

      {success && <SuccessMessage message={success} />}

      {/* ── Step 0: parking type ─────────────────────────────────────── */}
      {step === 0 && (
        <Card>
          <CardBody className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">How do visitors park here?</h2>
              <p className="text-sm text-gray-500 mt-1">Choose how visitors get a parking spot at this property.</p>
            </div>

            <div className="space-y-3">
              <OptionCard
                Icon={MapPin}
                title="Assigned Spots"
                description="Residents reserve a specific spot for their visitor."
                selected={state.parkingType === PARKING_TYPE.SPECIFIC_SPOT}
                onClick={() => update({ parkingType: PARKING_TYPE.SPECIFIC_SPOT, totalCapacity: '' })}
              />
              <OptionCard
                Icon={Users}
                title="Shared Spots"
                description="Residents reserve one of a limited number of spots. Visitors park in any vacant spot."
                selected={state.parkingType === PARKING_TYPE.LIMITED_OPEN}
                onClick={() => update({ parkingType: PARKING_TYPE.LIMITED_OPEN })}
              />
              <OptionCard
                Icon={CheckCircle2}
                title="No Reservation Needed"
                description="Visitors park anywhere, anytime without reserving a spot."
                selected={state.parkingType === PARKING_TYPE.FREE_PARKING}
                onClick={() => update({ parkingType: PARKING_TYPE.FREE_PARKING, totalCapacity: '' })}
              />
            </div>

            {state.parkingType === PARKING_TYPE.LIMITED_OPEN && (
              <FormField
                label="Number of available spots"
                type="number"
                value={state.totalCapacity}
                onChange={(e) => update({ totalCapacity: e.target.value })}
                placeholder="Example: 10"
                helpText="How many visitor spots are available at this property?"
              />
            )}
          </CardBody>
        </Card>
      )}

      {/* ── Step 1: reservation mode ─────────────────────────────────── */}
      {step === 1 && !isFreeFlow && (
        <Card>
          <CardBody className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">How do residents reserve?</h2>
              <p className="text-sm text-gray-500 mt-1">Choose how residents reserve visitor parking at this property.</p>
            </div>

            <div className="space-y-3">
              <OptionCard
                Icon={Calendar}
                title="Advance Reservation"
                description="Residents reserve a spot by picking a date, start time, and end time. They can do this ahead of time or on the day their visitor arrives."
                selected={state.requiresAdvanceReservation === true}
                onClick={() => update({ requiresAdvanceReservation: true })}
              />
              <OptionCard
                Icon={Clock}
                title="On-Arrival Check-In"
                description="Residents can only reserve a spot when their visitor arrives — no advance booking. Spots are first come, first served."
                selected={state.requiresAdvanceReservation === false}
                onClick={() => update({ requiresAdvanceReservation: false, reservationWindowDays: '' })}
              />
            </div>

            {state.requiresAdvanceReservation != null && (
              <FormField
                label={state.requiresAdvanceReservation === false
                  ? 'Maximum reservation length'
                  : 'Maximum reservation length (optional)'}
                type="number"
                value={state.maxReservationHours}
                onChange={(e) => update({ maxReservationHours: e.target.value })}
                placeholder="Example: 12"
                helpText={state.requiresAdvanceReservation === false
                  ? 'Set the longest a single reservation can last (in hours).'
                  : 'Set the longest a single reservation can last (in hours). Leave blank for no limit.'}
              />
            )}

            {state.requiresAdvanceReservation === true && (
              <FormField
                label="Reservation window (optional)"
                type="number"
                value={state.reservationWindowDays}
                onChange={(e) => update({ reservationWindowDays: e.target.value })}
                placeholder="Example: 30"
                helpText="How far in advance residents can reserve a spot (in days). Leave blank for no limit."
              />
            )}
          </CardBody>
        </Card>
      )}

      {/* ── Step 2: limits ────────────────────────────────────────────── */}
      {step === 2 && !isFreeFlow && (
        <Card>
          <CardBody className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Set reservation limits</h2>
              <p className="text-sm text-gray-500 mt-1">Control how often each unit and each visitor vehicle can reserve a spot.</p>
            </div>

            <LimitsSection
              title="Per unit"
              description="Limit how many reservations each unit can make."
              checked={state.perUnitEnabled}
              onToggle={() => update({ perUnitEnabled: !state.perUnitEnabled })}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  label="Reservations per week"
                  type="number"
                  value={state.perUnitWeekly}
                  onChange={(e) => update({ perUnitWeekly: e.target.value })}
                  placeholder="0"
                />
                <FormField
                  label="Reservations per month"
                  type="number"
                  value={state.perUnitMonthly}
                  onChange={(e) => update({ perUnitMonthly: e.target.value })}
                  placeholder="0"
                />
                <FormField
                  label="Active reservations at once"
                  type="number"
                  value={state.perUnitActive}
                  onChange={(e) => update({ perUnitActive: e.target.value })}
                  placeholder="0"
                />
              </div>
            </LimitsSection>

            <LimitsSection
              title="Per visitor vehicle"
              description="Limit how often the same license plate can be reserved."
              checked={state.perPlateEnabled}
              onToggle={() => update({ perPlateEnabled: !state.perPlateEnabled })}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Reservations per week"
                  type="number"
                  value={state.perPlateWeekly}
                  onChange={(e) => update({ perPlateWeekly: e.target.value })}
                  placeholder="0"
                />
                <FormField
                  label="Reservations per month"
                  type="number"
                  value={state.perPlateMonthly}
                  onChange={(e) => update({ perPlateMonthly: e.target.value })}
                  placeholder="0"
                />
              </div>
            </LimitsSection>

            <p className="text-xs text-gray-500">Leave any field blank to skip that limit.</p>
          </CardBody>
        </Card>
      )}

      {/* ── Step 3: rules ─────────────────────────────────────────────── */}
      {step === 3 && (
        <Card>
          <CardBody className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add parking rules <span className="text-gray-400 font-normal text-base">(optional)</span></h2>
              <p className="text-sm text-gray-500 mt-1">Write any instructions residents should see before reserving a spot.</p>
            </div>

            <FormTextarea
              label="Parking rules and instructions"
              value={state.rules}
              onChange={(e) => update({ rules: e.target.value })}
              placeholder={`Visitor parking is limited to 24 hours.\nVehicles must display a valid permit.\nUnauthorized vehicles may be towed.`}
              rows={6}
              helpText="Leave blank if you don't have any."
            />
          </CardBody>
        </Card>
      )}

      {/* ── Step 4: review ────────────────────────────────────────────── */}
      {step === 4 && (
        <Card>
          <CardBody className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Review</h2>
              <p className="text-sm text-gray-500 mt-1">Confirm everything looks right.</p>
            </div>

            <ReviewRow Icon={Car} title="Parking Type" onEdit={() => setStep(0)}>
              {state.parkingType === PARKING_TYPE.SPECIFIC_SPOT && 'Assigned Spots'}
              {state.parkingType === PARKING_TYPE.LIMITED_OPEN && (
                <>Shared Spots {state.totalCapacity ? `· ${state.totalCapacity} spots` : ''}</>
              )}
              {state.parkingType === PARKING_TYPE.FREE_PARKING && 'No Reservation Needed'}
            </ReviewRow>

            {!isFreeFlow && (
              <ReviewRow Icon={Calendar} title="Reservation Method" onEdit={() => setStep(1)}>
                {state.requiresAdvanceReservation === true ? 'Advance Reservation' : 'On-Arrival Check-In'}
                {state.maxReservationHours ? ` · Max ${state.maxReservationHours}h` : ''}
                {state.requiresAdvanceReservation === true && state.reservationWindowDays
                  ? ` · Up to ${state.reservationWindowDays} days ahead`
                  : ''}
              </ReviewRow>
            )}

            {!isFreeFlow && (
              <ReviewRow Icon={Users} title="Reservation Limits" onEdit={() => setStep(2)}>
                {state.perUnitEnabled || state.perPlateEnabled ? (
                  <div className="space-y-1">
                    {state.perUnitEnabled && (
                      <div className="text-sm">
                        Per unit:{' '}
                        {[
                          state.perUnitWeekly && `${state.perUnitWeekly}/wk`,
                          state.perUnitMonthly && `${state.perUnitMonthly}/mo`,
                          state.perUnitActive && `${state.perUnitActive} active`,
                        ].filter(Boolean).join(' · ') || '—'}
                      </div>
                    )}
                    {state.perPlateEnabled && (
                      <div className="text-sm">
                        Per vehicle:{' '}
                        {[
                          state.perPlateWeekly && `${state.perPlateWeekly}/wk`,
                          state.perPlateMonthly && `${state.perPlateMonthly}/mo`,
                        ].filter(Boolean).join(' · ') || '—'}
                      </div>
                    )}
                  </div>
                ) : (
                  'No limits set'
                )}
              </ReviewRow>
            )}

            <ReviewRow Icon={FileText} title="Parking Rules" onEdit={() => setStep(3)}>
              {state.rules?.trim() ? (
                <span className="line-clamp-3">{state.rules}</span>
              ) : (
                'No rules added'
              )}
            </ReviewRow>

            <ReviewRow Icon={Clock} title="Availability" onEdit={() => setStep(0)}>
              Always 24/7
            </ReviewRow>
          </CardBody>
        </Card>
      )}

      {error && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {/* Footer nav */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={goBack} icon={<ChevronLeft className="w-4 h-4" />}>
          Back
        </Button>
        {step === 4 ? (
          <Button
            variant="primary"
            onClick={handleFinish}
            loading={submitting}
          >
            {state.parkingType === PARKING_TYPE.SPECIFIC_SPOT ? '+ Add Spots' : 'Finish'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={goNext}
            disabled={
              (step === 0 && state.parkingType == null)
              || (step === 1 && state.requiresAdvanceReservation == null)
            }
            icon={<ChevronRight className="w-4 h-4" />}
            iconPosition="right"
          >
            Next
          </Button>
        )}
      </div>
    </div>
  )
}

// ── Helper components ────────────────────────────────────────────────

// eslint-disable-next-line no-unused-vars
const OptionCard = ({ Icon, title, description, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-xl border-2 p-4 flex items-start gap-3 transition-colors ${
      selected
        ? 'border-primary-500 bg-primary-50'
        : 'border-gray-200 bg-white hover:border-gray-300'
    }`}
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
      selected ? 'bg-primary-100' : 'bg-gray-100'
    }`}>
      <Icon className={`w-5 h-5 ${selected ? 'text-primary-600' : 'text-gray-500'}`} />
    </div>
    <div className="flex-1">
      <div className="font-semibold text-gray-900">{title}</div>
      <div className="text-sm text-gray-500 mt-0.5">{description}</div>
    </div>
    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
      selected ? 'border-primary-500' : 'border-gray-300'
    }`}>
      {selected && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
    </div>
  </button>
)

const LimitsSection = ({ title, description, checked, onToggle, children }) => (
  <div className={`rounded-xl border-2 p-4 ${checked ? 'border-primary-500' : 'border-gray-200'}`}>
    <FormCheckbox
      label={title}
      checked={checked}
      onChange={onToggle}
    />
    <div className="text-sm text-gray-500 mt-1 ml-6">{description}</div>
    {checked && <div className="mt-4 ml-6">{children}</div>}
  </div>
)

// eslint-disable-next-line no-unused-vars
const ReviewRow = ({ Icon, title, onEdit, children }) => (
  <button
    type="button"
    onClick={onEdit}
    className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-left"
  >
    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-primary-600" />
    </div>
    <div className="flex-1">
      <div className="font-semibold text-gray-900 mb-0.5">{title}</div>
      <div className="text-sm text-gray-600">{children}</div>
    </div>
    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-2" />
  </button>
)

export default ParkingSetupWizard
